import { ReactNode, Suspense, lazy, useEffect, useMemo } from "react";
import RoutePreloader from "./RoutePreloader";
import { startRouteLoading, finishRouteLoading } from "./routeLoading";
import { PAGES, ValidPaths } from "@shared/services/pages";

interface LazyRouteProps {
  componentPath: ValidPaths;
  fallback?: ReactNode;
}

function LazyRoute({ componentPath, fallback }: LazyRouteProps) {
  // Memoised per path: built fresh in render, `lazy()` got a new identity on every re-render of
  // this component — including one triggered by a state change miles away, like a toast — and
  // React treats a new lazy() as a new component type, remounting the whole screen under it.
  const LazyComponent = useMemo(
    () =>
      lazy(() =>
        PAGES[componentPath]().catch((error) => {
          finishRouteLoading();
          throw error;
        }),
      ),
    [componentPath],
  );

  useEffect(() => {
    startRouteLoading();
    return () => {
      finishRouteLoading();
    };
  }, [componentPath]);

  return (
    <Suspense fallback={fallback ?? <RoutePreloader />}>
      <LazyComponent />
    </Suspense>
  );
}

export default LazyRoute;
